import { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { dateCourte, getActifsCandidats, getActifsDeclares } from '@/shared/api/bridge';
import { CONSIGNE_AUCUN_DECLARE, refCourte } from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';
import RetraitDefinitif from '@/features/bridge/components/RetraitDefinitif';
import ListeCandidats from '@/features/bridge/components/ListeCandidats';

/**
 * Ce que l'agrégateur voit : les actifs déclarés, et ceux qu'on peut encore lui ouvrir.
 *
 * Les deux listes étaient empilées dans la même carte, la saisie en haut et le
 * résultat en dessous : on déclarait sans voir ce qui l'était déjà. Elles sont côte à
 * côte sur un écran large, l'une sous l'autre sur un téléphone.
 *
 * RETIRER EST DÉFINITIF DEPUIS LE 07/10/2026
 *
 * L'actif retiré reste attaché à cet agrégateur pour toujours. La corbeille en un clic
 * est remplacée par `RetraitDefinitif` : un motif écrit et le nom de l'agrégateur
 * ressaisi, gardés au journal d'audit. Le geste sert à une sortie réelle (fin de
 * mandat, actif déposé), jamais à déplacer un équipement.
 */
export default function OngletPerimetre({ agregateur, label, surChangement }) {
  const [declares, setDeclares] = useState(null);
  const [candidats, setCandidats] = useState(null);
  const [erreurDeclares, setErreurDeclares] = useState(null);
  const [erreurCandidats, setErreurCandidats] = useState(null);
  const [filtre, setFiltre] = useState('');
  const [aRetirer, setARetirer] = useState(null);

  // Deux lectures, deux erreurs : un refus sur les candidats ne doit pas cacher la
  // liste de ce qui est déjà déclaré, qui est la plus importante des deux.
  const charger = useCallback(async () => {
    setErreurDeclares(null);
    setErreurCandidats(null);
    await Promise.all([
      getActifsDeclares(agregateur).then(setDeclares).catch(setErreurDeclares),
      getActifsCandidats(agregateur)
        .then((r) => setCandidats(r?.candidats || []))
        .catch(setErreurCandidats),
    ]);
  }, [agregateur]);

  useEffect(() => { setDeclares(null); setCandidats(null); charger(); }, [charger]);

  const apresGeste = async () => {
    await charger();
    surChangement?.();
  };

  const declaresFiltres = useMemo(() => {
    const q = filtre.trim().toLowerCase();
    if (!declares || !q) return declares || [];
    return declares.filter((d) => [d.ref, d.type_declare].filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q)));
  }, [declares, filtre]);

  // `minmax(0, 1fr)` aussi sur téléphone : une piste implicite prend la largeur minimale
  // du tableau (482 px pour 358 disponibles, mesuré le 28/09) et le gabarit coupe le
  // reste sans barre de défilement. La moitié droite des candidats devenait invisible.
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-testid="onglet-perimetre">
      <section aria-labelledby="declares-titre" className="space-y-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="declares-titre" className="text-[14px] font-semibold text-neutral-900">
            Déclarés à {label}
            {declares && <span className="ml-1.5 font-normal tabular-nums text-neutral-500">{declares.length}</span>}
          </h3>
          {declares?.length > 8 && (
            <Input value={filtre} onChange={(e) => setFiltre(e.target.value)}
                   placeholder="filtrer par référence ou type" className="h-8 max-w-[220px]" />
          )}
        </div>

        <p className="flex items-center gap-1.5 text-[12px] text-neutral-500"
           data-testid="bridge-declares-definitif">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          Définitif : ces actifs restent chez {label}, même retirés.
        </p>
        <ErreurBloc erreur={erreurDeclares} testid="bridge-declares-erreur" />
        {declares === null && !erreurDeclares && <Skeleton className="h-40 w-full" />}

        {declares?.length === 0 && (
          <div className="rounded-md border border-dashed border-neutral-300 px-4 py-8 text-center
                          text-[13px] leading-relaxed text-neutral-500" data-testid="bridge-aucun-declare">
            Rien n&apos;est visible pour {label}.<br />
            {CONSIGNE_AUCUN_DECLARE}
          </div>
        )}

        {declaresFiltres.length > 0 && (
          <div className="max-h-[480px] overflow-auto rounded-md border border-neutral-200">
            <table className="w-full min-w-[440px] text-[13px]">
              <thead className="sticky top-0 bg-neutral-50 text-left text-[12px] text-neutral-500">
                <tr>
                  <th className="px-3 py-2 font-normal">Référence</th>
                  <th className="font-normal">Type</th>
                  <th className="whitespace-nowrap font-normal">Puissance max</th>
                  <th className="font-normal">Déclaré le</th>
                  <th className="w-20" />
                </tr>
              </thead>
              <tbody>
                {declaresFiltres.map((d) => (
                  <tr key={d.id} className="border-t border-neutral-100" data-testid={`bridge-declare-${d.asset_id}`}>
                    <td className="px-3 py-2"><code className="text-[12px]" title={d.ref}>{refCourte(d.ref)}</code></td>
                    <td>{d.type_declare}</td>
                    <td className="tabular-nums">
                      {d.specifications?.maxPower ? `${d.specifications.maxPower} W` : '—'}
                    </td>
                    <td className="whitespace-nowrap tabular-nums">{dateCourte(d.declaree_le)}</td>
                    <td className="pr-3 text-right">
                      <button type="button" onClick={() => setARetirer(d)}
                              className="text-[12.5px] text-neutral-500 underline-offset-2
                                         hover:text-red-700 hover:underline"
                              data-testid={`bridge-retrait-${d.asset_id}`}>
                        Retrait…
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ListeCandidats agregateur={agregateur} label={label} candidats={candidats}
                      erreur={erreurCandidats} surDeclaration={apresGeste} />

      <RetraitDefinitif agregateur={agregateur} label={label} declaration={aRetirer}
                        onFermer={() => setARetirer(null)} surRetrait={apresGeste} />
    </div>
  );
}
